import {
  type DocumentData,
  type Firestore,
  collection,
  doc,
  getDoc,
  serverTimestamp,
  setDoc,
  updateDoc,
} from 'firebase/firestore';
import { RouteSchema, RouteCreateInputSchema, RouteUpdateInputSchema, type Route, type RouteCreateInput, type RouteUpdateInput } from '@taki/data-model';
import { CURRENT_SCHEMA_VERSION, buildUpdateData, createConverter } from './standard-fields.js';
import { type CountOptions, type ListOptions, runCount, runList } from './list-query.js';
import { createArchiveOps } from './archive-ops.js';

/**
 * Repository de la famille `route` (tournée planifiée d'arrêts, org-wide).
 *
 * Réplique le PATRON `asset` : l'entrée est VALIDÉE par Zod
 * (`RouteCreateInputSchema`) AVANT toute écriture Firestore. On n'écrit jamais
 * un document pour découvrir ensuite qu'il est invalide. Les champs standard
 * sont remplis par le SDK, les requêtes `list` sont cadrées par orgId.
 */

export type { RouteCreateInput };

/** Nom de la collection Firestore de la famille route. */
export const ROUTES_COLLECTION = 'routes';

/**
 * Repository typé pour la famille `route`.
 * Toute lecture est validée par `RouteSchema`, toute écriture par
 * `RouteCreateInputSchema` (cage IA : aucun accès Firestore direct hors SDK).
 */
export interface RouteRepository {
  create(input: RouteCreateInput): Promise<Route>;
  update(id: string, patch: RouteUpdateInput): Promise<Route>;
  list(options?: ListOptions): Promise<Route[]>;
  count(options?: CountOptions): Promise<number>;
  get(id: string): Promise<Route | null>;
  /** Archive (`archivedAt`). Semantique commune : cf. archive-ops.ts. */
  archive(id: string): Promise<Route>;
  /** Desarchive (`archivedAt` = null, JAMAIS deleteField). Cf. archive-ops.ts. */
  unarchive(id: string): Promise<Route>;
  /** Supprime (`deletedAt` + `archivedAt`), invisible cote client. Cf. archive-ops.ts. */
  remove(id: string): Promise<Route>;
}

const routeConverter = createConverter<Route>((raw: DocumentData) => RouteSchema.parse(raw));

/**
 * @param getActorId - retourne l'uid de l'utilisateur authentifié courant
 *   (injecté par `createTakiSdk`). Les Security Rules exigent
 *   createdBy/updatedBy == request.auth.uid.
 */
export function createRouteRepository(
  db: Firestore,
  getActorId: () => string,
  orgId: string,
): RouteRepository {
  const col = collection(db, ROUTES_COLLECTION).withConverter(routeConverter);
  // Archiver, desarchiver, supprimer : la semantique des trois etats vit dans
  // `createArchiveOps` (archive-ops.ts), ecrite une seule fois pour les 55
  // familles. Une famille n'a plus rien a decider ici.
  const archiveOps = createArchiveOps<Route>(db, col, ROUTES_COLLECTION, getActorId);

  return {
    async create(input: RouteCreateInput): Promise<Route> {
      // Valider AVANT d'écrire : une entrée invalide échoue ici, rien n'atteint
      // Firestore. (Même doctrine que le patron asset.)
      const fields = RouteCreateInputSchema.parse(input);

      const actor = getActorId();
      const ref = doc(col);
      const now = new Date();

      // Champs métier optionnels : on ne les écrit que s'ils sont fournis
      // (évite d'ancrer des `undefined` / `null` parasites dans Firestore).
      const optional: DocumentData = {};
      if (fields.workerId !== undefined) optional['workerId'] = fields.workerId;
      if (fields.vehicleAssetId !== undefined) optional['vehicleAssetId'] = fields.vehicleAssetId;
      if (fields.shiftId !== undefined) optional['shiftId'] = fields.shiftId;
      if (fields.date !== undefined) optional['date'] = fields.date;
      if (fields.stops !== undefined) optional['stops'] = fields.stops;
      if (fields.status !== undefined) optional['status'] = fields.status;
      if (fields.proofDocumentIds !== undefined) optional['proofDocumentIds'] = fields.proofDocumentIds;
      if (fields.customFields !== undefined) optional['customFields'] = fields.customFields;

      const payload: DocumentData = {
        orgId: fields.orgId,
        type: fields.type,
        ...optional,
        createdAt: serverTimestamp(),
        updatedAt: serverTimestamp(),
        createdBy: actor,
        updatedBy: actor,
        archivedAt: null,
        deletedAt: null,
        schemaVersion: CURRENT_SCHEMA_VERSION,
      };

      await setDoc(ref, payload as unknown as Route);

      // setDoc ne résout pas serverTimestamp() : on valide une projection
      // cohérente avec ce qui vient d'être écrit.
      return RouteSchema.parse({
        id: ref.id,
        ...payload,
        createdAt: now,
        updatedAt: now,
      });
    },

    async update(id: string, patch: RouteUpdateInput): Promise<Route> {
      // Valider AVANT d'ecrire (meme doctrine que create).
      const fields = RouteUpdateInputSchema.parse(patch);
      const actor = getActorId();

      // Patch transforme pour updateDoc : customFields est eclate en chemins
      // pointes ('customFields.<cle>') pour fusionner cle par cle au lieu de
      // remplacer la map entiere (cf. buildUpdateData ; null = deleteField).
      const data = buildUpdateData(fields);
      data['updatedAt'] = serverTimestamp();
      data['updatedBy'] = actor;

      // Ref brute (sans converter) pour l'ecriture partielle : le converter
      // exigerait le document complet.
      await updateDoc(doc(db, ROUTES_COLLECTION, id), data);

      // Relire le document reel via la ref convertie : source de verite.
      const snap = await getDoc(doc(col, id));
      if (!snap.exists()) {
        throw new Error(`route ${id} introuvable apres mise a jour.`);
      }
      return snap.data();
    },

    async list(options?: ListOptions): Promise<Route[]> {
      // Cadrage orgId, exclusion des archives, sous-type, tri, taille de page
      // et curseur : toute la semantique vit dans `runList` (list-query.ts),
      // ecrite une seule fois pour les 55 familles.
      return runList(col, orgId, options);
    },

    async count(options?: CountOptions): Promise<number> {
      // Compter sans charger : meme cadrage que `list`, sans page ni tri
      // (cf. runCount, list-query.ts). Un compteur calcule sur la page
      // courante ment des que la liste est paginee.
      return runCount(col, orgId, options);
    },

    async get(id: string): Promise<Route | null> {
      const ref = doc(col, id);
      const snap = await getDoc(ref);
      if (!snap.exists()) return null;
      return snap.data();
    },

    // archive / unarchive / remove : cf. `archiveOps` plus haut.
    ...archiveOps,
  };
}
